import React, {useState, useEffect, useCallback} from 'react';
import {ComponentProps} from '@episerver/spa-core/EpiComponent';
import HomepageHeaderCarouselBlockData from 'Models/Content/HomePageHeaderCarouselNewVersionBlockData';
import HomepageHeaderCarouselItemData from 'Models/Content/HomepageHeaderCarouselItemNewVersionBlockData';
import HomepageTabBlockData from 'Models/Content/HomepageTabBlockData';
import SwiperCore, {Autoplay, EffectFade, Navigation} from 'swiper';
import {Swiper, SwiperSlide} from 'swiper/react';
import BackgroundVideo from '../../Page/TVLobbyPage/BackgroundVideo';
import classNames from 'classnames';
import 'swiper/swiper.scss';
import {useInView} from 'react-intersection-observer';
import {isMobile} from 'react-device-detect';
import {getImageProps, ImageProps, getVideoLink, getBackgroundImageProps, BackgroundImageProps} from 'app/utilities/ImageProps';
import {inActiveButton, activeButton} from './images/base64Images';
import MarketingTextBlockData from 'Models/Content/MarketingTextBlockData';
import TagName, {Tags} from 'app/Components/Shared/TagName';
import ContentSlider from '../ContentsCarrouselBlock/ContentSlider';
import {ContactUsFormBlockType} from 'Models/Content/ContactUsFormBlockData';

SwiperCore.use([Autoplay, EffectFade, Navigation]);

const TRANSITION_TIME = 5000;

type HomepageHeaderCarouselItem = {
  imageDesktop: ImageProps;
  imageMobile: ImageProps;
  videoDesktopUrl: string;
  videoMobileUrl: string;
  text: string;
  subtitle: string;
  buttonLink: string;
  buttonText: string;
  isWhiteText: boolean;
  titleHeadLineTag: number;
  subtitleHeadLineTag: number;
};

type HomepageTabItem = {
  title: string;
  icon: BackgroundImageProps;
};

const HomepageHeadeCarouselNewVersionBlock = ({data, context}: ComponentProps<HomepageHeaderCarouselBlockData>) => {
  const [animating, setAnimating] = useState(false);
  const [swiperInstence, setSwiperInstence] = useState<SwiperCore>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [fullScreen, setFullScreen] = useState(true);
  const [fullImage, setFullImage] = useState(true); // keep image at full size during transition to avoid mobile jumping in animations
  const [top, setTop] = useState<Number | null>(null);
  const {ref: topRef, inView: topInView, entry: topEntry} = useInView();
  const {ref: bottomRef, inView: bottomInView, entry: bottomEntry} = useInView();

  const items: HomepageHeaderCarouselItem[] = data.lItems.expandedValue.map((item: HomepageHeaderCarouselItemData) => {
    const text = !isMobile && item.title?.value ? item.desktopTitle?.value : item.title?.value;
    const subtitle = !isMobile && item.desktopSubtitle?.value ? item.desktopSubtitle?.value : item.subtitle?.value;
    const titleHeadLineTag = item.titleHeadlineTag?.value ? item.titleHeadlineTag?.value : 2;
    const subtitleHeadLineTag = item.subtitleHeadlineTag?.value ? item.subtitleHeadlineTag.value : 7;
    const imageDesktop = getImageProps(item.image, context);
    const imageMobile = getImageProps(item.mobileImage, context);
    const videoDesktopUrl = getVideoLink(item.video, context);
    const videoMobileUrl = getVideoLink(item.mobileVideo, context);
    const buttonLink = item.link?.value;
    const buttonText = item.linkTitle?.value;
    const isWhiteText = item.whiteColoredTitle?.value;
    return {
      imageDesktop,
      imageMobile,
      videoDesktopUrl,
      videoMobileUrl,
      text,
      subtitle,
      titleHeadLineTag,
      subtitleHeadLineTag,
      buttonLink,
      buttonText,
      isWhiteText,
    };
  });

  const tabs: HomepageTabItem[] = data.tabs?.expandedValue?.map((tab: HomepageTabBlockData) => {
    const title = tab.title?.value;
    const icon = getBackgroundImageProps(tab.backGroundImage, context);
    return {title, icon};
  });

  const anchorCallback = useCallback((node: HTMLDivElement) => {
    bottomRef(node);
    setTimeout(() => {
      if (node) setTop(node.offsetTop - window.innerHeight);
    });
  }, []);

  const getButtonClassByName = (index) => {
    let className = 'HomepageHeadeCarouselNewVersion__link';
    if (data.tabs?.expandedValue != null && data.tabs?.expandedValue.length > 0 && data.tabs?.expandedValue[index] != null) {
      var blockButton = (data.tabs.expandedValue[index] as any).blockButton.expandedValue;
      if (blockButton != null) {
        var designType = blockButton[0].buttonDesignMode.value;
        switch (designType) {
          case 0:
            className = className + ' HomepageHeadeCarouselNewVersion__link_purple';
            break;
          case 1:
            className = className + ' HomepageHeadeCarouselNewVersion__link_black';
            break;
          case 2:
            className = className + ' HomepageHeadeCarouselNewVersion__link_white';
            break;
        }
      }
      return className;
    }
  };

  const setButtonTextByIndexId = (index) => {
    if (data.tabs?.expandedValue != null && data.tabs?.expandedValue.length > 0 && data.tabs?.expandedValue[index] != null) {
      var blockButton = (data.tabs.expandedValue[index] as any).blockButton.expandedValue;
      if (blockButton != null) {
        var designText = blockButton[0].text.value;
        return designText;
      }
    }

    var buttonTextValue = (data.lItems.expandedValue[index] as any).linkTitle.value;
    return buttonTextValue;
  };
  return (
    <>
      <div ref={topRef} className='HomepageHeadeCarouselNewVersion__anchor'></div>
      <div
        className={`HomepageHeadeCarouselNewVersion ${fullScreen ? 'fullScreen' : ''}`}
        style={{transitionDuration: `${TRANSITION_TIME}ms`, marginTop: !fullScreen ? `${top ? (top as number) * 3 : null}px` : null}}>
        <Swiper
          navigation
          loop={true}
          spaceBetween={50}
          className={`HomepageHeadeCarouselNewVersion__swiper ${fullScreen ? 'fullScreen' : ''}`}
          onSwiper={(swiper) => {
            setSwiperInstence(swiper);
          }}
          autoplay={{delay: 5000,disableOnInteraction: true}}
          effect='slide'    
          onSlideChange={(swiper) => {
            setActiveIndex(swiper?.activeIndex);
          }}
          >
          {items?.map((item, i) => (
            <SwiperSlide key={i} className={classNames(`HomepageHeadeCarouselNewVersion__slide`, {fullImage})}>
              <BackgroundVideo
                videoUrl={isMobile ? item.videoMobileUrl : item.videoDesktopUrl}
                fallbackImageProps={isMobile ? item.imageMobile : item.imageDesktop}
              />
              <div className={classNames('HomepageHeadeCarouselNewVersion__contentContainer', {whiteText: item.isWhiteText})}>
                {item.text.length > 0 && (
                  <TagName tagName={item.titleHeadLineTag} isTitle className='HomepageHeadeCarouselNewVersion__title'>
                    <span dangerouslySetInnerHTML={{__html: item.text}}></span>
                  </TagName>
                )}
                {item.subtitle.length > 0 && (
                  <TagName tagName={item.subtitleHeadLineTag} className='HomepageHeadeCarouselNewVersion__subtitle'>
                    <span dangerouslySetInnerHTML={{__html: item.subtitle}}></span>
                  </TagName>
                )}

                {item.buttonLink?.length > 0 ? (
                  <a href={item.buttonLink} className={getButtonClassByName(i)}>
                    {setButtonTextByIndexId(i)}
                  </a>
                ) : (
                  ''
                )}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        <ul className={`HomepageHeadeCarouselNewVersion__Slides_Buttons ${fullScreen ? 'fullScreen' : ''}`}></ul>
      </div>
      <div
        ref={anchorCallback}
        className='HomepageHeadeCarouselNewVersion__anchor'
        style={{position: 'absolute', bottom: top ? `-${top ? (top as number) * 4 : null}px` : '-80px'}}></div>
    </>
  );
};

export default HomepageHeadeCarouselNewVersionBlock;
